import {
  useSessionStore,
} from "~/stores/session";
import {
  useCustomsCheckStore,
} from "~/stores/customsCheck";

const ROUTE_ROLES: Record<string, string[]> = {
  "/admin": ["AIL_ADMIN"],
  "/tenant-admin": ["TENANT_ADMIN", "EMPLOYER_ADMIN", "AIL_ADMIN"],
  "/user-performances": ["TENANT_MANAGER", "TENANT_ADMIN", "AIL_ADMIN"],
  "/employer": ["EMPLOYER_ADMIN", "EMPLOYER_MANAGER", "AIL_ADMIN"],
};

/**
 * Learner routes. They carry no role list of their own, so without this an
 * employer reaching them by bookmark or typed URL would load a training area
 * that is not theirs. Hiding the menu entries alone is not access control.
 */
const LEARNER_ROUTE_PREFIXES = [
  "/home",
  "/exercise",
  "/my-performance",
  "/my-credits-usage",
];

/**
 * Routes gated on a runtime flag rather than a role. The backend is the source
 * of truth and enforces the same rule, so this only saves the user landing on
 * a page that would fail every request.
 */
const FLAGGED_ROUTES: Record<string, () => Promise<boolean>> = {
  "/exercise/customs-check": () => useCustomsCheckStore().fetchAccess(),
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) {
    return;
  }

  const sessionStore = useSessionStore();

  if (!sessionStore.ready) {
    await sessionStore.initialize();
  }

  if (!sessionStore.isAuthenticated) {
    return navigateTo("/login");
  }

  await sessionStore.fetchRegistrationPaymentStatus();
  if (sessionStore.user?.status === "pending") {
    const allowed = ["/home", "/profile", "/my-credits-usage"];
    if (!allowed.includes(to.path)) {
      return navigateTo("/home");
    }
  }

  const userRoles = sessionStore.userRoles;
  const isEmployer = userRoles.includes("EMPLOYER_ADMIN") || userRoles.includes("EMPLOYER_MANAGER");

  if (isEmployer && LEARNER_ROUTE_PREFIXES.some((prefix) => to.path.startsWith(prefix))) {
    return navigateTo(resolveLandingRoute(userRoles));
  }

  for (const [prefix, allowedRoles] of Object.entries(ROUTE_ROLES)) {
    if (to.path.startsWith(prefix)) {
      const hasAccess = allowedRoles.some((r) => userRoles.includes(r));
      if (!hasAccess) {
        return navigateTo(resolveLandingRoute(userRoles));
      }
      break;
    }
  }

  for (const [prefix, check] of Object.entries(FLAGGED_ROUTES)) {
    if (to.path.startsWith(prefix)) {
      if (!(await check())) {
        return navigateTo("/home");
      }
      break;
    }
  }
});
